import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicyPage() {
  const lastUpdated = "12 Maggio 2025";

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Rarifex</title>
        <meta name="description" content="Informativa sulla privacy di Rarifex. Scopri come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali." />
        <link rel="canonical" href="https://www.rarifex.com/privacy" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Informativa sulla Privacy
            </h1>
            <p className="text-slate-500 mb-8">
              Ultimo aggiornamento: {lastUpdated}
            </p>

            <div className="prose prose-slate max-w-none">
              
              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Titolare del Trattamento</h2>
                <p className="text-slate-700 mb-4">
                  Il Titolare del trattamento dei dati personali è Rarifex S.r.l., con sede legale in Italia 
                  (di seguito "Rarifex", "noi" o "Titolare").
                </p>
                <p className="text-slate-700">
                  Per qualsiasi informazione relativa al trattamento dei dati personali, è possibile contattarci 
                  all'indirizzo email: <a href="mailto:privacy@rarifex.com" className="text-shop-primary hover:underline">privacy@rarifex.com</a>
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Dati Personali Raccolti</h2>
                <p className="text-slate-700 mb-4">
                  Rarifex raccoglie le seguenti categorie di dati personali:
                </p>
                
                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">2.1 Dati forniti volontariamente dall'utente</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Dati di registrazione: nome, cognome, indirizzo email, password</li>
                  <li>Dati di profilo: foto profilo, biografia, informazioni di contatto</li>
                  <li>Dati di spedizione: indirizzo di consegna, numero di telefono</li>
                  <li>Dati di fatturazione: ragione sociale, partita IVA, codice fiscale, indirizzo di fatturazione</li>
                  <li>Contenuti generati: messaggi, recensioni, richieste personalizzate</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">2.2 Dati raccolti automaticamente</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Dati di navigazione: indirizzo IP, tipo di browser, sistema operativo, pagine visitate</li>
                  <li>Dati di utilizzo: interazioni con la piattaforma, preferenze, cronologia ordini</li>
                  <li>Cookie e tecnologie similari (vedi Cookie Policy)</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">2.3 Dati di pagamento</h3>
                <p className="text-slate-700">
                  I dati di pagamento (numero carta di credito, CVV, data di scadenza) sono gestiti esclusivamente 
                  da Stripe, Inc. e non vengono mai trasmessi né memorizzati sui server di Rarifex. Per informazioni 
                  sul trattamento dei dati da parte di Stripe, consultare la 
                  <a href="https://stripe.com/it/privacy" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline"> Privacy Policy di Stripe</a>.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Finalità del Trattamento</h2>
                <p className="text-slate-700 mb-4">
                  I dati personali sono trattati per le seguenti finalità:
                </p>
                
                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.1 Esecuzione del contratto</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Creazione e gestione dell'account utente</li>
                  <li>Elaborazione degli ordini e gestione delle transazioni</li>
                  <li>Comunicazioni relative agli ordini (conferme, aggiornamenti di stato, spedizione)</li>
                  <li>Gestione delle richieste personalizzate e delle proposte degli artigiani</li>
                  <li>Assistenza clienti e gestione delle controversie</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.2 Adempimenti legali</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Adempimento di obblighi fiscali e contabili</li>
                  <li>Risposta a richieste delle autorità competenti</li>
                  <li>Prevenzione e contrasto di attività fraudolente</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.3 Legittimo interesse</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Miglioramento dei servizi e dell'esperienza utente</li>
                  <li>Sicurezza della piattaforma e prevenzione abusi</li>
                  <li>Analisi statistiche aggregate</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.4 Consenso (ove applicabile)</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Invio di comunicazioni di marketing e newsletter</li>
                  <li>Profilazione per suggerimenti personalizzati</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Base Giuridica del Trattamento</h2>
                <p className="text-slate-700 mb-4">
                  Il trattamento dei dati personali si fonda sulle seguenti basi giuridiche ai sensi dell'art. 6 del GDPR:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li><strong>Art. 6(1)(b):</strong> Esecuzione di un contratto o misure precontrattuali</li>
                  <li><strong>Art. 6(1)(c):</strong> Adempimento di obblighi legali</li>
                  <li><strong>Art. 6(1)(f):</strong> Legittimo interesse del Titolare</li>
                  <li><strong>Art. 6(1)(a):</strong> Consenso dell'interessato</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Destinatari dei Dati</h2>
                <p className="text-slate-700 mb-4">
                  I dati personali possono essere comunicati a:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li><strong>Artigiani:</strong> per l'esecuzione degli ordini (nome, indirizzo di spedizione, contatti)</li>
                  <li><strong>Fornitori di servizi:</strong> hosting, email, analytics, assistenza clienti</li>
                  <li><strong>Processori di pagamento:</strong> Stripe, Inc.</li>
                  <li><strong>Corrieri:</strong> per la consegna dei prodotti</li>
                  <li><strong>Autorità competenti:</strong> quando richiesto dalla legge</li>
                </ul>
                <p className="text-slate-700 mt-4">
                  I fornitori di servizi agiscono in qualità di Responsabili del trattamento ai sensi dell'art. 28 del GDPR 
                  e sono vincolati da appositi accordi contrattuali.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Trasferimento dei Dati Extra-UE</h2>
                <p className="text-slate-700 mb-4">
                  Alcuni fornitori di servizi potrebbero trattare dati al di fuori dello Spazio Economico Europeo. 
                  In tali casi, il trasferimento avviene sulla base di:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Decisioni di adeguatezza della Commissione Europea</li>
                  <li>Clausole Contrattuali Standard (SCC)</li>
                  <li>Altre garanzie appropriate ai sensi dell'art. 46 del GDPR</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Periodo di Conservazione</h2>
                <p className="text-slate-700 mb-4">
                  I dati personali sono conservati per il tempo strettamente necessario alle finalità per cui sono stati raccolti:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li><strong>Dati dell'account:</strong> per tutta la durata del rapporto contrattuale e fino a 10 anni dalla cessazione per obblighi legali</li>
                  <li><strong>Dati di fatturazione:</strong> 10 anni per obblighi fiscali</li>
                  <li><strong>Dati di navigazione:</strong> 24 mesi</li>
                  <li><strong>Comunicazioni di marketing:</strong> fino alla revoca del consenso</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Diritti dell'Interessato</h2>
                <p className="text-slate-700 mb-4">
                  Ai sensi degli articoli 15-22 del GDPR, l'interessato ha il diritto di:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li><strong>Accesso:</strong> ottenere conferma dell'esistenza di un trattamento e accedere ai propri dati</li>
                  <li><strong>Rettifica:</strong> ottenere la correzione di dati inesatti o l'integrazione di dati incompleti</li>
                  <li><strong>Cancellazione:</strong> ottenere la cancellazione dei dati nei casi previsti dalla legge</li>
                  <li><strong>Limitazione:</strong> ottenere la limitazione del trattamento nei casi previsti dalla legge</li>
                  <li><strong>Portabilità:</strong> ricevere i dati in formato strutturato e trasmetterli a un altro titolare</li>
                  <li><strong>Opposizione:</strong> opporsi al trattamento per motivi legittimi</li>
                  <li><strong>Revoca del consenso:</strong> revocare in qualsiasi momento il consenso prestato</li>
                </ul>
                <p className="text-slate-700 mt-4">
                  Per esercitare i propri diritti, l'interessato può inviare una richiesta a: 
                  <a href="mailto:privacy@rarifex.com" className="text-shop-primary hover:underline"> privacy@rarifex.com</a>
                </p>
                <p className="text-slate-700 mt-4">
                  L'interessato ha inoltre il diritto di proporre reclamo all'Autorità Garante per la Protezione 
                  dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">www.garanteprivacy.it</a>).
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Sicurezza dei Dati</h2>
                <p className="text-slate-700">
                  Rarifex adotta misure tecniche e organizzative adeguate per proteggere i dati personali da accessi 
                  non autorizzati, perdita, distruzione o divulgazione. Tali misure includono crittografia SSL/TLS, 
                  controlli di accesso, backup regolari e formazione del personale.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Modifiche alla Privacy Policy</h2>
                <p className="text-slate-700">
                  Il Titolare si riserva il diritto di modificare la presente informativa in qualsiasi momento. 
                  Le modifiche saranno comunicate tramite pubblicazione sul sito web. Si consiglia di consultare 
                  periodicamente questa pagina. L'uso continuato dei servizi dopo la pubblicazione delle modifiche 
                  costituisce accettazione delle stesse.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Contatti</h2>
                <p className="text-slate-700">
                  Per qualsiasi domanda relativa alla presente informativa o al trattamento dei dati personali:
                </p>
                <ul className="list-none pl-0 text-slate-700 mt-4 space-y-1">
                  <li><strong>Email:</strong> <a href="mailto:privacy@rarifex.com" className="text-shop-primary hover:underline">privacy@rarifex.com</a></li>
                  <li><strong>Email generale:</strong> <a href="mailto:info@rarifex.com" className="text-shop-primary hover:underline">info@rarifex.com</a></li>
                </ul>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
